import { useState, useEffect, useRef } from 'react';
import { AdminLayout } from './AdminDashboard';
import { Upload, Trash2, Copy, Image, Edit, Save, X, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { API_URL, formatDate } from '../../lib/utils';
import { authFetch } from '../../lib/auth';
import { toast } from 'sonner';

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await authFetch(`${API_URL}/media`);
      if (response.ok) {
        const data = await response.json();
        setMedia(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await authFetch(`${API_URL}/media`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          toast.success(`${file.name} uploadé`);
        } else {
          toast.error(`Erreur pour ${file.name}`);
        }
      } catch (error) {
        toast.error(`Erreur pour ${file.name}`);
      }
    }

    setUploading(false);
    fetchMedia();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) return;

    try {
      const response = await authFetch(`${API_URL}/media/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Fichier supprimé');
        fetchMedia();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdate = async () => {
    if (!selectedMedia) return;

    try {
      const params = new URLSearchParams();
      if (selectedMedia.alt_text) params.append('alt_text', selectedMedia.alt_text);
      if (selectedMedia.seo_title) params.append('seo_title', selectedMedia.seo_title);

      const response = await authFetch(`${API_URL}/media/${selectedMedia.id}?${params.toString()}`, {
        method: 'PUT',
      });

      if (response.ok) {
        toast.success('Média mis à jour');
        setSelectedMedia(null);
        fetchMedia();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copiée');
  };

  const filteredMedia = media.filter(m => 
    m.original_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.alt_text?.toLowerCase().includes(search.toLowerCase())
  );

  const isImage = (mimeType) => mimeType?.startsWith('image/');

  return (
    <AdminLayout>
      <div data-testid="admin-media">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#003366]">Médiathèque</h1>
            <p className="text-gray-500 mt-1">{media.length} fichiers</p>
          </div>
          <div className="mt-4 md:mt-0">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button asChild className="bg-[#DC143C] hover:bg-[#B01030] rounded-full cursor-pointer" disabled={uploading}>
                <span>
                  <Upload size={18} className="mr-2" />
                  {uploading ? 'Upload en cours...' : 'Ajouter des fichiers'}
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* Search */}
        <div className="admin-card mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Media Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredMedia.map((item) => (
            <div key={item.id} className="group relative bg-white rounded-xl overflow-hidden shadow-md">
              <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                {isImage(item.mime_type) ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                    alt={item.alt_text || item.original_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center p-4">
                    <Image className="text-gray-400 mx-auto mb-2" size={32} />
                    <p className="text-xs text-gray-500 truncate">{item.original_name}</p>
                  </div>
                )}
              </div>
              
              {/* Overlay actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => setSelectedMedia(item)} className="h-8 w-8 p-0">
                  <Edit size={14} />
                </Button>
                <Button size="sm" variant="secondary" onClick={() => copyUrl(item.url)} className="h-8 w-8 p-0">
                  <Copy size={14} />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} className="h-8 w-8 p-0">
                  <Trash2 size={14} />
                </Button>
              </div>

              <div className="p-2">
                <p className="text-xs text-gray-600 truncate">{item.original_name}</p>
                <p className="text-xs text-gray-400">{formatDate(item.uploaded_at)}</p>
              </div>
            </div>
          ))}
          {filteredMedia.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              Aucun fichier trouvé
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#003366]">
                Modifier le média
              </DialogTitle>
            </DialogHeader>
            
            {selectedMedia && (
              <div className="space-y-4">
                {isImage(selectedMedia.mime_type) && (
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${selectedMedia.url}`}
                      alt={selectedMedia.alt_text}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-[#003366] font-semibold">Texte alternatif (SEO)</Label>
                  <Input
                    value={selectedMedia.alt_text || ''}
                    onChange={(e) => setSelectedMedia({ ...selectedMedia, alt_text: e.target.value })}
                    className="mt-2"
                    placeholder="Description de l'image pour le SEO"
                  />
                </div>

                <div>
                  <Label className="text-[#003366] font-semibold">Titre SEO</Label>
                  <Input
                    value={selectedMedia.seo_title || ''}
                    onChange={(e) => setSelectedMedia({ ...selectedMedia, seo_title: e.target.value })}
                    className="mt-2"
                    placeholder="Titre optimisé pour le référencement"
                  />
                </div>

                <div>
                  <Label className="text-[#003366] font-semibold">URL</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={`${process.env.REACT_APP_BACKEND_URL}${selectedMedia.url}`}
                      readOnly
                      className="flex-1"
                    />
                    <Button onClick={() => copyUrl(selectedMedia.url)} variant="outline">
                      <Copy size={16} />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4 pt-4 border-t">
                  <Button onClick={() => setSelectedMedia(null)} variant="outline" className="flex-1">
                    Annuler
                  </Button>
                  <Button onClick={handleUpdate} className="flex-1 bg-[#003366] hover:bg-[#002244]">
                    <Save size={16} className="mr-2" /> Enregistrer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
